import { useEffect, useLayoutEffect, useRef, type RefObject } from "react";
import { NavigationType, useLocation, useNavigationType } from "react-router";

/** En fazla bu kadar geçmiş girdisinin konumu hatırlanıyor; en eskisi düşüyor. */
const REMEMBERED_ENTRIES = 50;

/**
 * Geri dönülen ekranın içeriği henüz o kadar uzun değilse (önbellekteki veri ve tembel yüklenen
 * bloklar birkaç yüz milisaniyede yerleşiyor) konum aralıklarla yeniden deneniyor: en çok
 * `RESTORE_TRIES` × `RESTORE_INTERVAL_MS`. Kare değil zaman: arka plandaki sekmede
 * `requestAnimationFrame` hiç çalışmıyor. Okuyucu bu arada kaydırırsa (tekerlek, dokunma, tuş)
 * deneme bırakılıyor — okuyucuyla kavga edilmez.
 */
const RESTORE_TRIES = 12;
const RESTORE_INTERVAL_MS = 50;
const READER_SCROLL_EVENTS = ["wheel", "touchstart", "pointerdown", "keydown"] as const;

/**
 * EKRAN DEĞİŞİNCE `<main>`'İN KAYDIRMASI VE ODAĞI (9 Ekim 2026, 3. tur — patron görev turu).
 *
 * KAYAN ÖĞE `window` DEĞİL `<main>`, ve `<main>` ekranlar arasında YAŞIYOR. Tarayıcının «yeni
 * sayfa üstten açılır, geri tuşu yerine döner» davranışı yalnız pencerenin kaydırmasında var;
 * burada hiçbiri yoktu. Ölçülen iki kusur:
 *   - Panelim 275 px aşağı kaydırılmışken raydan «İzin Yönetimi» açıldı: yeni ekran 104 px
 *     aşağıda açıldı — başlık ve sekmeler görünmüyordu (önceki ekranın kaydırması taşınıyordu).
 *   - Panelim'de bir sayaca basıp geri dönünce sayfa en üstte açıldı; okuyucu yerini kaybetti.
 * Kural: YENİ ekran (PUSH/REPLACE) en üstten açılır, GERİ/İLERİ (POP) ekranın bırakıldığı yerden.
 * Yalnız ADRES YOLU değişince: çekmece (`?panel=`), filtre ve sayfa numarası aynı ekranın içinde
 * kalır, kaydırmaya dokunulmaz. Adreste çapa (`#izin-takvimi`) varsa ekran onu kendisi
 * gösteriyor (`scroll-to-anchor`) — burada sıfırlanmıyor.
 *
 * KONUM KAYDIRDIKÇA YAZILIYOR, AYRILIRKEN DEĞİL. Ayrılma anında okunan `scrollTop` yeni ekranın
 * DOM'u yerleştikten sonraki değer (kısa sayfa onu kırpıyor); tek bir dinleyici her kaydırmayı
 * o an ekrandaki girdiye (`location.key`) yazıyor ve girdi düzen etkisinde, olay gelmeden
 * önce değişiyor.
 *
 * ODAK KAYBOLDUYSA `<main>`'E (accessibility.md › Focus). Basılan sayaç, sonuç satırı ya da kart
 * eski ekranla birlikte DOM'dan kalkınca odak `<body>`'ye düşüyordu: klavyeyle gezen kişinin
 * sonraki Tab'ı belgenin en başından — «İçeriğe atla», üst çubuk, ray — başlıyordu. Odak
 * `<main>`'e (programla, kaydırmadan) konuyor; sonraki Tab yeni ekranın içinden devam ediyor.
 * Odak hâlâ bir yerdeyse (raydaki bağlantı, çekmece, açılan bir panel) DOKUNULMUYOR: rayda
 * ok tuşlarıyla gezen ya da sayfanın kendi otomatik odağı kazanıyor. `<main>` kontur almıyor
 * (`app.css` onu dışarıda tutuyor).
 */
export function useScreenScroll(ref: RefObject<HTMLElement | null>): void {
  const location = useLocation();
  const navigationType = useNavigationType();
  const positions = useRef(new Map<string, number>());
  const current = useRef<{ key: string; pathname: string } | null>(null);

  useEffect(() => {
    const main = ref.current;
    if (!main) return undefined;
    const remember = () => {
      const entry = current.current;
      if (!entry) return;
      // Sil-yaz: Map ekleme sırasını tutuyor, en son kaydırılan en sona geçiyor.
      positions.current.delete(entry.key);
      positions.current.set(entry.key, main.scrollTop);
      if (positions.current.size > REMEMBERED_ENTRIES) {
        const oldest = positions.current.keys().next().value;
        if (oldest !== undefined) positions.current.delete(oldest);
      }
    };
    main.addEventListener("scroll", remember, { passive: true });
    return () => main.removeEventListener("scroll", remember);
  }, [ref]);

  useLayoutEffect(() => {
    const main = ref.current;
    const previous = current.current;
    current.current = { key: location.key, pathname: location.pathname };
    if (!main || previous === null || previous.pathname === location.pathname) return undefined;

    const active = document.activeElement;
    if (active === null || active === document.body) main.focus({ preventScroll: true });

    if (location.hash) return undefined;
    const target =
      navigationType === NavigationType.Pop ? (positions.current.get(location.key) ?? 0) : 0;
    main.scrollTop = target;
    if (target === 0) return undefined;

    let tries = 0;
    let timer = 0;
    const stop = () => {
      tries = RESTORE_TRIES;
      window.clearTimeout(timer);
    };
    const retry = () => {
      if (main.scrollTop >= target - 1 || tries >= RESTORE_TRIES) return;
      tries += 1;
      main.scrollTop = target;
      timer = window.setTimeout(retry, RESTORE_INTERVAL_MS);
    };
    for (const type of READER_SCROLL_EVENTS) main.addEventListener(type, stop, { passive: true });
    timer = window.setTimeout(retry, RESTORE_INTERVAL_MS);
    return () => {
      window.clearTimeout(timer);
      for (const type of READER_SCROLL_EVENTS) main.removeEventListener(type, stop);
    };
  }, [location.key, location.pathname, location.hash, navigationType, ref]);
}
